import * as React from 'react';
import api from '../../../store/api';
import { getPasswordStrength, PasswordStrength } from '../../../common/utility'
import ResetCodeComponent from '../../shared/ResetCodeComponent'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { logOut } from '../../../store/modules/authSlice';
import { useForm, Controller } from "react-hook-form";
import { useState, useRef } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Grid, { GridSpacing } from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import throttle from 'lodash/throttle';


//Interfaces
interface User {
    firstName: string | undefined,
    lastName: string,
    emailAddress: string,
    phoneNumber: string,
    email: string,
    userId: string,
}

//Styles
const useStyles = makeStyles((theme: any) => ({
    button: {
        width: '100%',
        border: '1px solid #979797',
    },
    fieldTitle: {
        marginBottom: 5,
        minHeight: 25
    },
    exitIcon: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 5,
    },
    textFieldInput: {
        backgroundColor: '#ECECEC',
        color: 'black',
        height: 44,
    },
    dialogPaper: {
        minWidth: 500,
        //height: 500,
        margin: 0,
        position: 'absolute',
        borderRadius: 0,
        shadow: 'none',
        right: 2,
        top: 59
    },
    email: {
        fontFamily: "'Lato', sans-serif;",
        fontSize: 18,
    },
    loading: {
        width: '100%',

    },
    pHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    pLabels: {
        marginTop: 30,
        marginRight: 40,
    },
    eHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    rHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    form: {
        marginBottom: 10,
    },
    headingAlert: {
        padding: '0px 30px 10px 30px',
    },
    alertRoot: {
        marginTop: 0,
    },
    inputs: {
        backgroundColor: "#ebebeb",
        margin: '0px 10px 0px 10px',
        borderRadius: '4px',
        width: '54px',
        height: '65px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '32px',
        color: '#212121',
        position: 'relative',
        cursor: 'text',
    }
}));

// Shared Functions
const CreateLoadingLine = (props: any): JSX.Element => {
    const classes = useStyles();
    return (
        <div className={classes.loading}>
            <LinearProgress color="primary" />
        </div>
    )
}


const UserPaneModal = (props: any) => {

    //Global
    const { handleSubmit, watch, errors, control, formState, setValue, getValues, setError, clearErrors } = useForm();
    const { open, onClose, thisUserId } = props;
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");
    const [widget, setWidget] = useState(1);
    const [newEmail, setNewEmail] = React.useState("");


    //Main Widget
    const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

    //Password
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(PasswordStrength.none);
    const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    let passwordWatch = watch('password');
    const [showPasswordTwo, setShowPasswordTwo] = useState(false);
    const codeInput = useRef<ResetCodeComponent>(null);

    //Change Widget
    const changeWidget = (value: number) => {
        clearErrors();
        setErrorMessage("");
        setStatusMessage("");
        setWidget(value);
    }

    const changeEmail = (value: string) => {
        setNewEmail(value);
    }

    //Reset Messages
    const resetMessages = () => {
        setErrorMessage("");
        setStatusMessage("");
    }

    // User Pane Widget Functions
    const getDetails = React.useMemo(() => throttle(async (): Promise<void> => {
        resetMessages();
        await api.getCurrentUserDetails("version="+Math.random()+Math.random()+Math.random()).then(_res => {
            if (_res !== null && _res.data.model !== null) {
                setUserDetails(_res.data.model);
            }
        }).catch(error => {
            setErrorMessage('Error while getting user details');
        });
    }), []);

    const user: User = {
        firstName: '',
        lastName: '',
        emailAddress: '', // Fixed by Ankit (Please revert if needed)
        phoneNumber: '',
        email: '',
        userId: '',
    }
    const [userDetails, setUserDetails] = useState<User>(user);

    const updateContact = async (): Promise<void> => {
        resetMessages();
        setLoading(true);
        let values = getValues();
        let _req: { [index: string]: any } = {
            userId: userDetails.userId,
            firstName: values['firstName'],
            lastName: values['lastName'],
            phoneNumber: values['phoneNumber'],
        };

        await api.updateMyUserDetails('', _req)
            .then(res => {
                setLoading(false);
                if (!res.hasError && res.message !== null)
                    onClose();
                else
                    setErrorMessage(res.message);
            })
            .catch(error => {
                setErrorMessage(error);
                setLoading(false);
            });
    }
    React.useEffect(() => {

        getDetails();

    }, []);

    // Reset Password  Functions
    const updatePassword = async (): Promise<void> => {
        resetMessages();
        setLoading(true);
        let values = getValues();

        let _req: { [index: string]: any } = {
            userId: userDetails.userId,
            currentPassword: values['currentPassword'],
            password: values['password'],
        };

        await api.changePasswordUserPane('', _req)
            .then(res => {
                if (!res.hasError) {
                    //handleClose();
                    setWidget(1);
                    setStatusMessage(res.message ?? "");
                }

                if (res.hasError)
                    setErrorMessage(res.message ?? "");

                setLoading(false);
            })
            .catch(error => {
                setErrorMessage(error);
                setLoading(false);
            });
        setLoading(false);
    }
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };
    const handleClickShowPasswordTwo = () => {
        setShowPasswordTwo(!showPasswordTwo);
    };
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };
    const handleMouseDownPasswordTwo = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };
    const handleMouseDownConfirmedPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };
    const handleClickShowConfirmedPassword = () => {
        setShowConfirmedPassword(!showConfirmedPassword);
    };
    const updatePasswordStrengtLabel = () => {
        let fontColor = 'red';
        if (passwordStrength === PasswordStrength.good) {
            fontColor = 'green';
        }
        else if (passwordStrength === PasswordStrength.moderate) {
            fontColor = 'orange';
        }
        return (
            <label style={{ color: fontColor, backgroundColor: '#fff' }}> {passwordStrength} </label >
        )
    }

    // Reset Email Functions
    const updateEmail = async (): Promise<void> => {
        resetMessages();
        setLoading(true);
        let values = getValues();


        let _req: { [index: string]: any } = {
            userId: userDetails.userId,
            emailAddress: values['newEmail'],
            password: values['password'],
        };


        await api.validateEmailUniqueUserPane('', _req)
            .then(async res => {
                setNewEmail(values['newEmail'].toString());
                if (!res.data.hasError) {
                    await api.requestEmailChangeUserPane('', _req)
                        .then(res => {
                            if (res.data.hasError === true)
                                setErrorMessage(res.data.message);
                            else {

                                changeWidget(4);
                            }

                        })
                }
                else if (res.data.hasError) {
                    setErrorMessage("Email already exists");
                }
                setLoading(false);
            })
            .catch(error => {
                setErrorMessage(error);
                setLoading(false);
            });
        setLoading(false);
    }

    // Reset Email Functions
    const confirmCode = async (): Promise<void> => {
        resetMessages();
        setLoading(true);
        let values = getValues();

        let _req: { [index: string]: any } = {
            emailAddress: newEmail,
            code: codeInput.current?.state.value ?? '',
        };

        console.log(_req);

        await api.validateEmailChangeUserPane('', _req)
            .then(async res => {
                if (res.data.hasError === true) {
                    setErrorMessage(res.data.message);
                    console.log(res);
                    console.log(res.data.message);
                }
                else {
                    setStatusMessage(res.data.message ?? "");
                    getDetails();
                    changeWidget(1);

                }

            })
            .catch(error => {
                setErrorMessage(error);
                setLoading(false);
                alert("Check this");
            });
        setLoading(false);


    }

    return (
        <Dialog classes={{ paper: classes.dialogPaper }} BackdropProps={{ style: { backgroundColor: 'rgba(76, 175, 80, 0.0)' } }} maxWidth="sm" open={open} onClose={onClose} aria-labelledby="form-dialog-title">

            {widget === 1 ? (

                //Show Main Widget
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                        <form
                            onSubmit={handleSubmit(updateContact)}
                            autoComplete="off"
                            id="addCustomerForm">
                            <DialogContent>
                                <div className={classes.exitIcon}>
                                    <ClearIcon onClick={onClose} style={{ cursor: "pointer" }} />
                                </div>
                                <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                        <div className={classes.fieldTitle}>First Name</div>
                                        <Controller
                                            control={control}
                                            as={
                                                <TextField
                                                    InputProps={{ classes: { root: classes.textFieldInput } }}
                                                    variant='outlined'
                                                    error={errors.firstName}
                                                    helperText={errors.firstName ? errors.firstName.message : ""}
                                                />}
                                            name='firstName'
                                            defaultValue={userDetails?.firstName}
                                            rules={{
                                                required: "First Name is required",
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={classes.fieldTitle}>Last Name</div>
                                        <Controller
                                            control={control}
                                            as={
                                                <TextField
                                                    InputProps={{ classes: { root: classes.textFieldInput } }}
                                                    variant='outlined'
                                                    error={errors.lastName}
                                                    helperText={errors.lastName ? errors.lastName.message : ""}
                                                />}
                                            name='lastName'
                                            defaultValue={userDetails?.lastName}
                                            rules={{
                                                required: "Last Name is required",
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={classes.fieldTitle}>Mobile Phone</div>
                                        <Controller
                                            control={control}
                                            as={
                                                <TextField
                                                    InputProps={{ classes: { root: classes.textFieldInput } }}
                                                    variant='outlined'
                                                    error={errors.name}
                                                    helperText={errors.phoneNumber ? errors.phoneNumber.message : ""}
                                                />}
                                            name='phoneNumber'
                                            defaultValue={userDetails?.phoneNumber}
                                            rules={{
                                                required: "Mobiler Number is required",
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            disabled={formState.isSubmitting}
                                            type='submit'
                                            className={classes.button}
                                            style={{ fontWeight: 'bold', marginTop: 30 }} color="primary">
                                            Save Changes
                                        </Button>

                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </form>

                        <DialogContent style={{ padding: '8px, 32px' }}>
                            <hr style={{ height: 1, borderWidth: 0, color: '#D8D8D8', backgroundColor: '#D8D8D8' }}></hr>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <div onClick={() => changeWidget(3)} className={classes.fieldTitle}>Email<a style={{ marginLeft: 20, color: '#02489D', cursor: "pointer" }}>Change Email</a></div>
                                    <div className={classes.email}>{userDetails?.emailAddress}</div>
                                </Grid>
                            </Grid>
                            <hr style={{ height: 1, borderWidth: 0, color: '#D8D8D8', backgroundColor: '#D8D8D8' }}></hr>
                        </DialogContent>
                    </div>
                    <div>
                        <DialogActions>
                            <Grid container spacing={3}>
                                <Grid item xs={6} style={{ marginBottom: 10, }}>
                                    <Button onClick={() => changeWidget(2)} className={classes.button} style={{ backgroundColor: '#fff', color: '#333333' }} color="primary">
                                        Change Password
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button onClick={() => { dispatch(logOut()) }} className={classes.button} style={{ backgroundColor: '#fff', color: '#333333' }} color="primary">
                                        Sign Out
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogActions>

                        <div className={classes.headingAlert}>
                            <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertRoot }} style={{ display: errorMessage ? 'flex' : 'none' }}>
                                {errorMessage}
                            </Alert>
                        </div>

                        <Grid container >
                            <Grid item xs={12}>
                                <div>
                                    {loading ? <CreateLoadingLine /> : null}
                                </div>
                            </Grid>
                        </Grid>
                    </div>
                </div>

            ) : widget === 2 ? (

                //Show Password Reset Widget
                <div>
                    <form
                        onSubmit={handleSubmit(updatePassword)}
                        autoComplete="off"
                        id="addCustomerForm">
                        <DialogContent>
                            <div className={classes.pHeader}>
                                <div className={classes.exitIcon}>
                                    <ArrowBackIcon onClick={() => changeWidget(1)} style={{ cursor: "pointer" }} />
                                </div>
                                <div className={classes.exitIcon}>
                                    <ClearIcon onClick={onClose} style={{ cursor: "pointer" }} />
                                </div>
                            </div>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <h1 style={{ fontWeight: 900, fontSize: 32, margin: 0 }}>Change Password</h1>
                                </Grid>
                            </Grid>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <div className={classes.fieldTitle}>Your Current Password</div>
                                    <Controller
                                        defaultValue=''
                                        control={control}
                                        as={<TextField InputProps={{
                                            classes: {
                                                root: classes.textFieldInput
                                            },
                                            endAdornment: <InputAdornment position="end">
                                                <IconButton style={{ color: '#b5b5b5' }}
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}>
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        }} variant='outlined' type={showPassword ? 'text' : 'password'} error={errors.currentPassword} helperText={errors.currentPassword ? errors.currentPassword.message : ""} />}
                                        name='currentPassword'
                                        rules={{
                                            required: "Current Password is required"
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <div className={classes.fieldTitle}>New Password</div>
                                    <Controller
                                        control={control}
                                        defaultValue=''
                                        render={({ onChange, onBlur, value, name }) => (<TextField InputProps={{
                                            classes: {
                                                root: classes.textFieldInput
                                            },
                                            endAdornment: <InputAdornment position="end">
                                                <IconButton style={{ color: '#b5b5b5' }}
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPasswordTwo}
                                                    onMouseDown={handleMouseDownPasswordTwo}>
                                                    {showPasswordTwo ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        }}
                                            onBlur={onBlur}
                                            value={value}
                                            name={name}
                                            onChange={e => {
                                                let pwdStrength = getPasswordStrength(e.target.value);
                                                setPasswordStrength(pwdStrength);
                                                onChange(e)
                                            }}
                                            variant='outlined' type={showPasswordTwo ? 'text' : 'password'} error={errors.password} helperText={errors.password ? errors.password.message : ""} />)}
                                        name='password'
                                        rules={{
                                            required: "Password is required",
                                            validate: (value) => {
                                                let pwdStrength = getPasswordStrength(value);
                                                if (pwdStrength !== PasswordStrength.good) {
                                                    return false;
                                                }
                                                else {
                                                    return true;
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <div className={classes.fieldTitle}>Confirm new Password</div>
                                    <Controller
                                        control={control}
                                        defaultValue=''
                                        as={<TextField InputProps={{
                                            classes: {
                                                root: classes.textFieldInput
                                            },
                                            endAdornment: <InputAdornment position="end">
                                                <IconButton style={{ color: '#b5b5b5' }}
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowConfirmedPassword}
                                                    onMouseDown={handleMouseDownConfirmedPassword}>
                                                    {showConfirmedPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        }} variant='outlined' type={showConfirmedPassword ? 'text' : 'password'} error={errors.confirmedPassword} helperText={errors.confirmedPassword ? errors.confirmedPassword.message : ""} />}
                                        name='confirmedPassword'
                                        rules={{
                                            required: "Confirmed Password is required",
                                            validate: (value) => {
                                                if (passwordWatch === value) {
                                                    return true;
                                                }
                                                else {
                                                    return "Confirmed Password does not match"
                                                }
                                            }
                                        }}
                                    />
                                    <div className={classes.pLabels} style={{ marginTop: 10 }}>
                                        <label>Password strength:{' '}</label>
                                        {updatePasswordStrengtLabel()}

                                    </div>
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Button
                                        disabled={formState.isSubmitting}
                                        type='submit'
                                        className={classes.button}
                                        style={{ fontWeight: 'bold' }} color="primary">
                                        Next
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </form>

                    <div className={classes.headingAlert}>
                        <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertRoot }} style={{ display: errorMessage ? 'flex' : 'none' }}>
                            {errorMessage}
                        </Alert>
                        <Alert variant="filled" severity="success" classes={{ root: classes.alertRoot }} style={{ display: statusMessage ? 'flex' : 'none' }}>
                            {statusMessage}
                        </Alert>
                    </div>

                    <Grid container >
                        <Grid item xs={12}>
                            <div>
                                {loading ? <CreateLoadingLine /> : null}
                            </div>
                        </Grid>
                    </Grid>
                </div>

            ) : widget === 3 ? (

                //Show Email Change Widget
                <div>
                    <form
                        autoComplete="off"
                        onSubmit={handleSubmit(updateEmail)}
                        id="addCustomerForm">
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                                <DialogContent>
                                    <div className={classes.eHeader}>
                                        <div onClick={() => changeWidget(1)} className={classes.exitIcon}>
                                            <ArrowBackIcon style={{ cursor: "pointer" }} />
                                        </div>
                                        <div onClick={onClose} className={classes.exitIcon}>
                                            <ClearIcon style={{ cursor: "pointer" }} />
                                        </div>
                                    </div>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <h1 style={{ fontWeight: 900, fontSize: 32, margin: 0 }}>Change your email address</h1>
                                        </Grid>
                                    </Grid>
                                </DialogContent>

                                <DialogContent>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <div className={classes.fieldTitle}>Your Password</div>
                                            <Controller
                                                defaultValue=''
                                                control={control}
                                                as={<TextField InputProps={{
                                                    classes: {
                                                        root: classes.textFieldInput
                                                    },
                                                    endAdornment: <InputAdornment position="end">
                                                        <IconButton style={{ color: '#b5b5b5' }}
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}>
                                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }} variant='outlined' type={showPassword ? 'text' : 'password'} error={errors.password} helperText={errors.password ? errors.password.message : ""} />}
                                                name='password'
                                                rules={{
                                                    required: "Current Password is required"
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </DialogContent>

                                <DialogContent>
                                    <Grid item xs={12}>
                                        <div className={classes.fieldTitle}>New Email</div>
                                        <Controller
                                            control={control}
                                            as={
                                                <TextField
                                                    InputProps={{ classes: { root: classes.textFieldInput } }}
                                                    variant='outlined'
                                                    error={errors.newEmail}
                                                    helperText={errors.newEmail ? errors.newEmail.message : ""}
                                                />}
                                            name='newEmail'
                                            rules={{
                                                required: "A valid email address is required",
                                            }}
                                        />
                                    </Grid>
                                </DialogContent>
                            </div>
                            <div>
                                <DialogActions style={{ marginBottom: 10 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Button
                                                disabled={formState.isSubmitting}
                                                type='submit'
                                                className={classes.button}
                                                style={{ fontWeight: 'bold' }} color="primary">
                                                Next
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </DialogActions>


                                <div className={classes.headingAlert}>
                                    <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertRoot }} style={{ display: errorMessage ? 'flex' : 'none' }}>
                                        {errorMessage}
                                    </Alert>
                                    <Alert variant="filled" severity="success" classes={{ root: classes.alertRoot }} style={{ display: statusMessage ? 'flex' : 'none' }}>
                                        {statusMessage}
                                    </Alert>
                                </div>

                                <Grid container >
                                    <Grid item xs={12}>
                                        <div>
                                            {loading ? <CreateLoadingLine /> : null}
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>

                        </div>
                    </form>
                </div>

            ) : widget === 4 ? (

                //Show Email Change Confirmation Widget
                <div>
                    <form
                        autoComplete="off"
                        onSubmit={handleSubmit(confirmCode)}
                        id="addCustomerForm">
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                                <DialogContent>
                                    <div className={classes.rHeader}>
                                        <div onClick={() => changeWidget(3)} className={classes.exitIcon}>
                                            <ArrowBackIcon style={{ cursor: "pointer" }} />
                                        </div>
                                        <div onClick={onClose} className={classes.exitIcon}>
                                            <ClearIcon style={{ cursor: "pointer" }} />
                                        </div>
                                    </div>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <h1 style={{ fontWeight: 900, }}>Enter verification code</h1>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={6}>
                                        <Grid item xs={12}>
                                            <div className={classes.fieldTitle}>We just sent a verification code to {newEmail}. Enter it here.</div>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <div className={classes.fieldTitle} style={{ marginTop: 20, marginBottom: 20, }}>Verification Code</div>
                                            <div className={classes.form}>
                                                <ResetCodeComponent ref={codeInput} className={classes.inputs} />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </DialogContent>
                            </div>
                            <div>
                                <DialogActions>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Button
                                                disabled={formState.isSubmitting}
                                                type='submit'
                                                className={classes.button}
                                                style={{ fontWeight: 'bold' }} color="primary">
                                                Done
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </DialogActions>

                                <div className={classes.headingAlert}>
                                    <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertRoot }} style={{ display: errorMessage ? 'flex' : 'none' }}>
                                        {errorMessage}
                                    </Alert>
                                    <Alert variant="filled" severity="success" classes={{ root: classes.alertRoot }} style={{ display: statusMessage ? 'flex' : 'none' }}>
                                        {statusMessage}
                                    </Alert>
                                </div>

                                <Grid container >
                                    <Grid item xs={12}>
                                        <div>
                                            {loading ? <CreateLoadingLine /> : null}
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </div>
                    </form>
                </div>

            ) : (

                null

            )}

        </Dialog>
    );
}

export default React.memo(UserPaneModal);